import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { ArrowLeft, Download, FileText } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function LessonPlanView() {
  const { planId } = useParams();
  const navigate = useNavigate();
  const [lessonPlan, setLessonPlan] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLessonPlan();
  }, [planId]);

  const fetchLessonPlan = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/teacher/lesson-plans/${planId}`);
      setLessonPlan(response.data);
    } catch (error) {
      toast.error('Failed to load lesson plan');
      navigate('/lesson-plans');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (format) => {
    try {
      const response = await axios.get(
        `${API_URL}/api/teacher/lesson-plans/${planId}/download/${format}`,
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `lesson_plan_${planId}.${format === 'pdf' ? 'pdf' : 'docx'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`Lesson plan downloaded as ${format.toUpperCase()}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Download failed. Please ensure you have an active subscription.');
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-sky-50">Loading...</div>;
  if (!lessonPlan) return null;

  return (
    <div className="min-h-screen bg-sky-50" data-testid="lesson-plan-view-page">
      <header className="glass-header py-4 px-6">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <Button onClick={() => navigate('/lesson-plans')} variant="ghost" data-testid="back-to-lesson-plans">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Lesson Plans
          </Button>
          <div className="flex gap-2">
            <Button
              onClick={() => handleDownload('pdf')}
              className="bg-teal-600 hover:bg-teal-700 text-white"
              data-testid="download-pdf-button"
            >
              <Download className="w-4 h-4 mr-2" /> PDF
            </Button>
            <Button
              onClick={() => handleDownload('word')}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="download-word-button"
            >
              <Download className="w-4 h-4 mr-2" /> Word
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-12">
        <div className="glass-card p-8 mb-6">
          <div className="flex items-start justify-between mb-6">
            <div>
              <h1 className="text-4xl font-bold text-slate-900 mb-2">{lessonPlan.subject}</h1>
              <p className="text-lg text-slate-600">{lessonPlan.grade} - {lessonPlan.duration}</p>
            </div>
            <span className={`px-4 py-2 rounded-full text-sm font-medium ${
              lessonPlan.status === 'final'
                ? 'bg-green-100 text-green-700'
                : 'bg-amber-100 text-amber-700'
            }`}>
              {lessonPlan.status}
            </span>
          </div>

          <div className="space-y-8">
            {lessonPlan.lessons.map((lesson, index) => (
              <div key={lesson.lesson_id} className="border-t border-slate-200 pt-6">
                <div className="flex items-start justify-between mb-4">
                  <h2 className="text-2xl font-bold text-teal-700">
                    Lesson {index + 1}: {lesson.topic}
                  </h2>
                  <span className="px-3 py-1 bg-teal-100 text-teal-700 rounded-full text-sm font-medium">
                    {lesson.time_per_lesson || 60} minutes
                  </span>
                </div>

                <div className="space-y-6">
                  {lesson.learning_objectives && lesson.learning_objectives.length > 0 && (
                    <div>
                      <h3 className="text-lg font-semibold text-slate-900 mb-2">Learning Objectives</h3>
                      <ul className="list-disc list-inside text-slate-700 bg-blue-50 p-4 rounded-lg space-y-1">
                        {lesson.learning_objectives.map((obj, i) => (
                          <li key={i}>{obj}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {lesson.skills_to_develop && lesson.skills_to_develop.length > 0 && (
                    <div>
                      <h3 className="text-lg font-semibold text-slate-900 mb-2">Skills to be Developed</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {lesson.skills_to_develop.map((skill, i) => (
                          <div key={i} className="flex items-center text-slate-700 bg-purple-50 p-3 rounded-lg">
                            <span className="w-2 h-2 bg-purple-600 rounded-full mr-2"></span>
                            {skill}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-2">Background Knowledge</h3>
                    <p className="text-slate-700 bg-slate-50 p-4 rounded-lg">
                      {lesson.background_knowledge}
                    </p>
                  </div>

                  {lesson.new_words && lesson.new_words.length > 0 && (
                    <div>
                      <h3 className="text-lg font-semibold text-slate-900 mb-2">New Words</h3>
                      <div className="flex flex-wrap gap-2">
                        {lesson.new_words.map((word, i) => (
                          <span key={i} className="px-3 py-1 bg-teal-100 text-teal-700 rounded-full text-sm font-medium">
                            {word}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  <div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-2">Teacher Activity</h3>
                    <div className="text-slate-700 bg-slate-50 p-4 rounded-lg whitespace-pre-wrap">
                      {lesson.teacher_activity}
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-2">Learner Activity</h3>
                    <p className="text-slate-700 bg-slate-50 p-4 rounded-lg">
                      {lesson.learner_activity}
                    </p>
                  </div>

                  {lesson.assessment_questions && lesson.assessment_questions.length > 0 && (
                    <div>
                      <h3 className="text-lg font-semibold text-slate-900 mb-2">Assessment Questions</h3>
                      <div className="space-y-3">
                        {lesson.assessment_questions.map((q, i) => (
                          <div key={i} className="bg-slate-50 p-4 rounded-lg">
                            <p className="font-medium text-slate-900 mb-1">{i + 1}. {q.question}</p>
                            <div className="flex gap-3 text-sm text-slate-600">
                              <span className="px-2 py-1 bg-white rounded">Level: {q.cognitive_level}</span>
                              <span className="px-2 py-1 bg-white rounded">Marks: {q.marks}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {lesson.expanded_opportunities && (
                    <div>
                      <h3 className="text-lg font-semibold text-slate-900 mb-2">Expanded Opportunities</h3>
                      <p className="text-slate-700 bg-slate-50 p-4 rounded-lg">
                        {lesson.expanded_opportunities}
                      </p>
                    </div>
                  )}

                  {lesson.language_integration && (
                    <div>
                      <h3 className="text-lg font-semibold text-slate-900 mb-2">Language Integration</h3>
                      <p className="text-slate-700 bg-slate-50 p-4 rounded-lg">
                        {lesson.language_integration}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
}